import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import superAdminDataEntryService from '../superAdminDataEntryService';
import type {
    CreatePromptRequest,
    UpdatePromptRequest,
    UpdateWorkflowDefinitionRequest,
} from '../../types/superAdminDataEntry.type';
import { queryKeys } from './queryKeys';

/**
 * Hook to fetch customize page data (prompts, tenants, agent configs)
 */
export const useGetCustomizePage = () => {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: queryKeys.customizePage,
        queryFn: superAdminDataEntryService.getCustomizePage,
    });

    return {
        customizePageData: data,
        isCustomizePageLoading: isLoading,
        customizePageError: error,
        refetchCustomizePage: refetch,
    };
};

/**
 * Hook to create a new prompt
 */
export const useCreatePrompt = () => {
    const queryClient = useQueryClient();

    const {
        mutateAsync: createPrompt,
        isPending,
        ...rest
    } = useMutation({
        mutationFn: (promptData: CreatePromptRequest) => superAdminDataEntryService.createPrompt(promptData),
        onSuccess: () => {
            // Invalidate and refetch customize page data
            queryClient.invalidateQueries({ queryKey: queryKeys.customizePage });
        },
    });

    return { createPrompt, isCreatingPrompt: isPending, ...rest };
};

/**
 * Hook to update an existing prompt
 */
export const useUpdatePrompt = () => {
    const queryClient = useQueryClient();

    const {
        mutateAsync: updatePrompt,
        isPending,
        ...rest
    } = useMutation({
        mutationFn: ({ promptId, promptData }: { promptId: string; promptData: UpdatePromptRequest }) =>
            superAdminDataEntryService.updatePrompt(promptId, promptData),
        onSuccess: () => {
            // Invalidate and refetch customize page data
            queryClient.invalidateQueries({ queryKey: queryKeys.customizePage });
        },
    });

    return { updatePrompt, isUpdatingPrompt: isPending, ...rest };
};

/**
 * Hook to delete a prompt
 */
export const useDeletePrompt = () => {
    const queryClient = useQueryClient();

    const {
        mutateAsync: deletePrompt,
        isPending,
        ...rest
    } = useMutation({
        mutationFn: (promptId: string) => superAdminDataEntryService.deletePrompt(promptId),
        onSuccess: () => {
            // Invalidate and refetch customize page data
            queryClient.invalidateQueries({ queryKey: queryKeys.customizePage });
        },
    });

    return { deletePrompt, isDeletingPrompt: isPending, ...rest };
};

/**
 * Hook to update a workflow definition
 */
export const useUpdateWorkflowDefinition = () => {
    const queryClient = useQueryClient();

    const {
        mutateAsync: updateWorkflowDefinition,
        isPending,
        ...rest
    } = useMutation({
        mutationFn: (data: UpdateWorkflowDefinitionRequest) =>
            superAdminDataEntryService.updateWorkflowDefinition(data),
        onSuccess: () => {
            // Invalidate and refetch customize page data
            queryClient.invalidateQueries({ queryKey: queryKeys.customizePage });
        },
    });

    return { updateWorkflowDefinition, isUpdatingWorkflowDefinition: isPending, ...rest };
};
